import i18next from 'i18next';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { axiosClient as axios } from '../../../utils/axios_instance';
import { openSuccessNotification } from '../../../common/notification/Notification';
import { COMPANY_SAMPLING_TYPE_URL, GLOBAL_SAMPLING_TYPE_URL } from '../../../config/config.api';

import { CompanySamplingType, CompanySamplingTypeState, GlobalSamplingType } from './interfaces';

const initialState: CompanySamplingTypeState = {
  globalSamplingTypes: [],
  companySamplingTypes: [],
  isLoadingGlobalSamplingTypes: false,
  isLoadingCompanySamplingTypes: false,
  showCreateSamplingTypeModal: false,
  showEditSamplingTypeModal: false,
  isCreatingSamplingType: false,
  isEditingSamplingType: false,
};

export const companySamplingTypeSlice = createSlice({
  name: 'companySamplingType',
  initialState,
  reducers: {
    setGlobalSamplingTypes: (state: CompanySamplingTypeState, action: PayloadAction<GlobalSamplingType[]>) => {
      state.globalSamplingTypes = action.payload;
    },
    setCompanySamplingTypes: (state: CompanySamplingTypeState, action: PayloadAction<CompanySamplingType[]>) => {
      state.companySamplingTypes = action.payload;
    },
    
    setIsLoadingGlobalSamplingTypes: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.isLoadingGlobalSamplingTypes = action.payload;
    },
    setIsLoadingCompanySamplingTypes: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.isLoadingCompanySamplingTypes = action.payload;
    },

    setShowCreateSamplingTypeModal: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.showCreateSamplingTypeModal = action.payload;
    },
    setIsCreatingSamplingType: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.isCreatingSamplingType = action.payload;
    },

    setCompanySamplingType: (state: CompanySamplingTypeState, action: PayloadAction<CompanySamplingType>) => {
      state.companySamplingType = action.payload;
    },
    setShowEditSamplingTypeModal: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.showEditSamplingTypeModal = action.payload;
    },
    setIsEditingSamplingType: (state: CompanySamplingTypeState, action: PayloadAction<boolean>) => {
      state.isEditingSamplingType = action.payload;
    },
  },
});

export const {
  setGlobalSamplingTypes,
  setCompanySamplingTypes,
  setIsLoadingGlobalSamplingTypes,
  setIsLoadingCompanySamplingTypes,
  setShowCreateSamplingTypeModal,
  setIsCreatingSamplingType,
  setCompanySamplingType,
  setShowEditSamplingTypeModal,
  setIsEditingSamplingType,
} = companySamplingTypeSlice.actions;

export const fetchGlobalSamplingTypes = () => async (dispatch: Function) => {
  const params = {
    $limit: -1,
  };

  dispatch(setIsLoadingGlobalSamplingTypes(true));

  try {
    const response = await axios.get<GlobalSamplingType[]>(GLOBAL_SAMPLING_TYPE_URL, { params });
    dispatch(setGlobalSamplingTypes(response.data));
    dispatch(setIsLoadingGlobalSamplingTypes(false));
  } catch (e) {
    console.log(e?.response);
  }
};

export const fetchCompanySamplingTypes = (props: {phaseType: string;}) => async (dispatch: Function) => {
  const { phaseType } = props;

  const params = {
    $limit: -1,
    '$sort[active]': -1,
    phaseType,
  };

  dispatch(setIsLoadingCompanySamplingTypes(true));

  try {
    const response = await axios.get<CompanySamplingType[]>(COMPANY_SAMPLING_TYPE_URL, { params });
    dispatch(setCompanySamplingTypes(response.data));
    dispatch(setIsLoadingCompanySamplingTypes(false));
  } catch (e) {
    console.log(e?.response);
  }
};

export const createCompanySamplingType = (props: { body: CompanySamplingType; onSuccess: () => void; }) => async (dispatch: Function) => {
  const { body, onSuccess } = props;
  dispatch(setIsCreatingSamplingType(true));

  try {
    await axios.post(COMPANY_SAMPLING_TYPE_URL, body);
    dispatch(fetchCompanySamplingTypes({ phaseType: body.phaseType }));
    onSuccess();
    openSuccessNotification(i18next.t('samplingTypes.created'));
  } catch (e) {
    console.log(e?.response);
  }

  dispatch(setIsCreatingSamplingType(false));
};

export const updateCompanySamplingType = (props: { body: CompanySamplingType; onSuccess: () => void; }) => async (dispatch: Function) => {
  const { body, onSuccess } = props;
  dispatch(setIsEditingSamplingType(true));
  const url = `${COMPANY_SAMPLING_TYPE_URL}/${body._id}`;

  try {
    await axios.patch(url, body);
    dispatch(fetchCompanySamplingTypes({ phaseType: body.phaseType }));
    onSuccess();
    openSuccessNotification(i18next.t('samplingTypes.updated'));
  } catch (e) {
    console.log(e?.response);
  }

  dispatch(setIsEditingSamplingType(false));
};

export default companySamplingTypeSlice.reducer;
